import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ProcessTaskStatusEditorModalLifecycleQuery,
  ProcessTaskStatusEditorModalLifecycleQueryVariables,
} from 'processes/dashboard/bulk-edit/__generated/ProcessTaskStatusEditorModalLifecycleQuery';
import {TASK_STATUS_ICON_STYLES, TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import {findValidOutStatuses, sortTaskStatuses} from 'processes/status/TaskStatusUtil';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';

import {ProcessTaskListingGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {ProcessTaskStatusDisplay} from '../ProcessTaskDashboardDisplay';

type ProcessTaskStatus = ProcessTaskListingGraphQL['status'];

export interface ProcessTaskStatusEditorModalContentsProps {
  selectedStatus: ProcessTaskStatus;
  currentStatus: ProcessTaskStatus;
  options: Array<ProcessTaskStatus>;
  onUpdate: (status: ProcessTaskStatus) => void;
}

interface ProcessTaskStatuEditorModalProps {
  onRequestSubmit: (status: ProcessTaskStatus) => void;
  onRequestClose: () => void;
  processTaskStatusLifecycleId: string;
  processTaskStatus: ProcessTaskStatus;
}

export function ProcessTaskStatusEditorModalContents(
  props: ProcessTaskStatusEditorModalContentsProps
): JSX.Element {
  return (
    <div className={css(styles.container)}>
      <span className={css(styles.field)}>
        <span className={css(styles.label)}>Current Value:</span>
        <span className={css(styles.current)}>
          <div className="u-ellipsis-text">{props.currentStatus.displayName}</div>
        </span>
      </span>
      <span className={css(styles.field)}>
        <span className={css(styles.label)}>New Value:</span>
        <SelectDropdown
          displayName={<ProcessTaskStatusDisplay status={props.selectedStatus} />}
          menu={
            <Menu
              value={props.selectedStatus.id}
              height={Menu.HEIGHTS.MEDIUM}
              width={Menu.WIDTHS.FULL}
              scrollable={true}
            >
              {props.options.map((status: ProcessTaskStatus, idx) => (
                <Option
                  value={status.id}
                  key={status.id}
                  icon={TASK_STATUS_ICONS[status.statusType].icon}
                  iconClassName={css(TASK_STATUS_ICON_STYLES[status.statusType])}
                  onSelect={() => {
                    props.onUpdate(status);
                  }}
                >
                  {status.displayName}
                </Option>
              ))}
            </Menu>
          }
        />
      </span>
    </div>
  );
}

export const PROCESS_TASK_STATUS_EDITOR_MODAL_LIFECYCLE_QUERY = gql`
  query ProcessTaskStatusEditorModalLifecycleQuery($processTaskStatusLifecycleId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskStatusLifecycle(id: $processTaskStatusLifecycleId) {
      ... on ProcessTaskStatusLifecycleNode {
        ...ProcessTaskStatusLifecycleGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingProcessTaskStatusEditorModal(
  props: ProcessTaskStatuEditorModalProps
): JSX.Element {
  const [selectedStatus, setSelectedStatus] = useState(props.processTaskStatus);
  return (
    <Modal
      title="Bulk update task status"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(selectedStatus);
      }}
      buttons={[
        {
          text: 'Submit',
          submit: true,
        },
      ]}
    >
      <BenchlingQuery<
        ProcessTaskStatusEditorModalLifecycleQuery,
        ProcessTaskStatusEditorModalLifecycleQueryVariables
      >
        query={PROCESS_TASK_STATUS_EDITOR_MODAL_LIFECYCLE_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{processTaskStatusLifecycleId: props.processTaskStatusLifecycleId}}
      >
        {(data) =>
          isInaccessible(data.processTaskStatusLifecycle) ? (
            <WorkspaceItemInaccessibleGuard
              inaccessibleInfo={data.processTaskStatusLifecycle}
              modelType="processTaskStatusLifecycle"
            />
          ) : (
            <ProcessTaskStatusEditorModalContents
              selectedStatus={selectedStatus}
              currentStatus={props.processTaskStatus}
              options={sortTaskStatuses(
                findValidOutStatuses(props.processTaskStatus, data.processTaskStatusLifecycle)
              )}
              onUpdate={setSelectedStatus}
            />
          )
        }
      </BenchlingQuery>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
  },
  field: {
    flexGrow: 1,
  },
  label: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  current: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
  },
});
